import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  makeStyles,
  Modal,
  Paper,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import MicIcon from '@material-ui/icons/Mic';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { toast } from 'react-toastify';

import whatsBackground from '../../assets/wa-background.png';
import whatsBackgroundDark from '../../assets/wa-background-dark.png';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useDate } from '../../hooks/useDate';
import { useInternalChatVoiceRecorder } from '../../hooks/useInternalChatVoiceRecorder';
import api from '../../services/api';
import {
  getInternalChatMediaKind,
  stripInternalChatMessageCaption,
} from '../../utils/internalChatMedia';
import { getInternalChatUnreadForUser } from '../../utils/internalChatSync';
import { i18n } from '../../translate/i18n';
import InternalChatAttachmentPreviewModal from '../../components/InternalChatAttachmentPreviewModal';
import InternalChatMessageMedia from '../../components/InternalChatMessageMedia';
import InternalChatVoiceNote from '../../components/InternalChatVoiceNote';

const waLight = (theme) => (theme?.mode ?? theme?.palette?.type) === 'light';

function formatRecordingClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const z = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${z(m)}:${z(s)}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: waLight(theme) ? '#f0f2f5' : '#202c33',
    borderBottom: waLight(theme) ? '1px solid #e9edef' : '1px solid #2a3942',
    minHeight: 56,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  backBtn: {
    marginLeft: theme.spacing(-0.5),
    color: waLight(theme) ? '#54656f' : '#aebac1',
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: waLight(theme) ? 'rgba(11, 20, 26, 0.06)' : 'rgba(255, 255, 255, 0.08)',
    },
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.2,
    color: waLight(theme) ? '#111b21' : '#e9edef',
  },
  headerSubtitle: {
    fontSize: '0.8125rem',
    color: waLight(theme) ? '#667781' : '#8696a0',
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  messageList: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '20px 20px 30px 20px',
    ...theme.scrollbarStyles,
    backgroundImage: waLight(theme) ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: waLight(theme) ? 'transparent' : '#0b0b0d',
    fontFamily: theme.typography.fontFamily,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    marginBottom: 10,
    maxWidth: 'min(92%, 600px)',
  },
  rowMine: {
    marginLeft: 'auto',
    flexDirection: 'row-reverse',
  },
  messageColumn: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'min(92%, 600px)',
    minWidth: 0,
  },
  messageColumnMine: {
    alignItems: 'flex-end',
  },
  messageColumnTheirs: {
    alignItems: 'flex-start',
  },
  timeBelowVoice: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
    color: '#667781',
    marginTop: 4,
    lineHeight: 1.2,
    maxWidth: 300,
    fontFamily: theme.typography.fontFamily,
  },
  timeBelowVoiceMine: {
    alignSelf: 'flex-end',
    textAlign: 'right',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0,
  },
  timeBelowVoiceTheirs: {
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  senderAboveVoice: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: '#6bcbef',
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: theme.typography.fontFamily,
  },
  bubble: {
    padding: '8px 12px 8px 12px',
    borderRadius: 8,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    fontFamily: theme.typography.fontFamily,
    '& p': { margin: 0 },
  },
  bubbleIncoming: {
    backgroundColor: waLight(theme) ? '#ffffff' : '#202c33',
    color: waLight(theme) ? '#303030' : '#ffffff',
    boxShadow: waLight(theme) ? '0 1px 0.5px rgba(0,0,0,0.13)' : '0 1px 2px rgba(0,0,0,0.3)',
    alignSelf: 'flex-start',
  },
  bubbleOutgoing: {
    backgroundColor: waLight(theme) ? '#d9fdd3' : '#005c4b',
    color: waLight(theme) ? '#303030' : '#ffffff',
    boxShadow: waLight(theme) ? '0 1px 0.5px rgba(0,0,0,0.13)' : '0 1px 2px rgba(0,0,0,0.3)',
    alignSelf: 'flex-end',
  },
  senderName: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    marginBottom: 4,
    color: '#6bcbef',
    fontFamily: theme.typography.fontFamily,
  },
  messageContent: {
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    letterSpacing: '-0.011em',
    fontWeight: 400,
    wordBreak: 'break-word',
    color: 'inherit',
    fontFamily: theme.typography.fontFamily,
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      borderRadius: 8,
      marginTop: 8,
    },
  },
  messageCaptionPlain: {
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    letterSpacing: '-0.011em',
    fontWeight: 400,
    wordBreak: 'break-word',
    color: 'inherit',
    marginBottom: 4,
    fontFamily: theme.typography.fontFamily,
  },
  timeCaption: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
    color: '#667781',
    textAlign: 'right',
    marginTop: 4,
    lineHeight: 1.2,
    fontFamily: theme.typography.fontFamily,
  },
  timeCaptionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0,
    marginTop: 4,
  },
  messageDeleteIcon: {
    padding: 4,
    marginLeft: 2,
    color: '#667781',
    '&:hover': {
      color: '#ea0038',
      backgroundColor: 'rgba(234, 0, 56, 0.08)',
    },
  },
  composer: {
    flexShrink: 0,
    padding: '12px 16px',
    borderTop: waLight(theme) ? '1px solid #e9edef' : '1px solid #2a3942',
    backgroundColor: waLight(theme) ? '#f0f2f5' : '#202c33',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  '@keyframes internalChatMicPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 },
  },
  composerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  composerInputShell: {
    flex: 1,
    minWidth: 0,
    borderRadius: 8,
    backgroundColor: waLight(theme) ? '#ffffff' : '#2a3942',
    border: waLight(theme) ? '1px solid #e9edef' : '1px solid #3d4a53',
  },
  composerTextField: {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-input': {
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '-0.011em',
    },
  },
  /** Cronômetro fora do campo branco (perto dos botões de gravar/cancelar). */
  recordingChronoOutside: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    padding: '4px 2px',
    marginRight: 2,
  },
  recordingDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#ea0038',
    flexShrink: 0,
    animation: '$internalChatMicPulse 1s ease-in-out infinite',
  },
  recordingTime: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: waLight(theme) ? '#111b21' : '#e9edef',
    fontVariantNumeric: 'tabular-nums',
  },
  recorderActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    gap: 2,
    minHeight: 40,
  },
  /** Slot fixo para o Tooltip+IconButton não mudar a altura da linha ao trocar mic/enviar. */
  composerIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    flexShrink: 0,
    boxSizing: 'border-box',
    lineHeight: 0,
  },
  actionIconMuted: {
    color: waLight(theme) ? '#54656f' : '#aebac1',
  },
  cancelRecordIcon: {
    color: '#ea0038',
  },
  confirmRecordIcon: {
    color: '#00a884',
  },
  avatarSm: {
    width: 32,
    height: 32,
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    maxWidth: '90vw',
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '80vh',
    marginBottom: theme.spacing(2),
  },
}));

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function resolveOwnerName(chat) {
  if (!chat) return '';
  if (chat.owner?.name) return chat.owner.name;
  if (Array.isArray(chat.users) && chat.ownerId != null) {
    const row = chat.users.find((u) => u.userId === chat.ownerId || u.user?.id === chat.ownerId);
    return row?.user?.name || '';
  }
  return '';
}

export default function ChatMessages({
  chat,
  onBack,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
  handleDeleteMessage,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();
  const messageListRef = useRef(null);
  /** Evita scroll ao carregar página anterior (prepend); só quando entra mensagem nova no fim */
  const messageScrollTailRef = useRef({ len: 0, lastId: null });

  const [contentMessage, setContentMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreviewModalOpen, setAttachmentPreviewModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const composerInputElRef = useRef(null);
  const shouldRestoreComposerFocusRef = useRef(false);
  const contentMessageRef = useRef('');
  const handleSendMessageRef = useRef(handleSendMessage);

  useEffect(() => {
    contentMessageRef.current = contentMessage;
  }, [contentMessage]);

  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  const { isRecording, recordingSeconds, startRecording, confirmRecording, cancelRecording } =
    useInternalChatVoiceRecorder({
      onFile: (file) => {
        const t = contentMessageRef.current.trim();
        shouldRestoreComposerFocusRef.current = true;
        handleSendMessageRef.current(t, file);
        setContentMessage('');
        setAttachmentFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (err) => {
        if (err?.message === 'unsupported') {
          toast.info(i18n.t('chatInternal.media.recordUnsupported'));
        } else {
          toast.error(i18n.t('chatInternal.media.recordError'));
        }
      },
    });

  useEffect(() => {
    if (!attachmentFile) setAttachmentPreviewModalOpen(false);
  }, [attachmentFile]);

  const handleStartVoice = () => {
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    startRecording();
  };

  const scrollToBottom = useCallback(() => {
    const el = messageListRef.current;
    if (!el) {
      baseRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
      return;
    }
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  const unreadCount = useMemo(
    () => getInternalChatUnreadForUser(chat, user.id),
    [chat, user.id]
  );

  useLayoutEffect(() => {
    messageScrollTailRef.current = { len: 0, lastId: null };
  }, [chat?.id]);

  useLayoutEffect(() => {
    const list = Array.isArray(messages) ? messages : [];
    const len = list.length;
    const lastId = len > 0 ? list[len - 1]?.id : null;
    const prev = messageScrollTailRef.current;
    const grew = len > prev.len;
    const lastChanged = lastId != null && lastId !== prev.lastId;
    if (grew && lastChanged) {
      scrollToBottom();
    }
    messageScrollTailRef.current = { len, lastId };
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (unreadCount > 0 && chat?.id) {
      api.post(`/chats/${chat.id}/read`).catch(() => {});
    }
    scrollToBottomRef.current = scrollToBottom;
  }, [chat?.id, unreadCount, scrollToBottom, scrollToBottomRef]);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo?.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const handleSend = () => {
    const t = contentMessage.trim();
    const file = attachmentFile;
    if (!t && !file) return;
    shouldRestoreComposerFocusRef.current = true;
    handleSendMessage(t, file || undefined);
    setContentMessage('');
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!shouldRestoreComposerFocusRef.current) return;
    if (loading || isRecording || attachmentPreviewModalOpen) return;
    requestAnimationFrame(() => composerInputElRef.current?.focus?.());
    shouldRestoreComposerFocusRef.current = false;
  }, [loading, isRecording, attachmentPreviewModalOpen]);

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  useEffect(() => {
    const nodes = document.querySelectorAll('.internal-chat-msg img, .internal-chat-media-preview');
    const handlers = [];
    nodes.forEach((img) => {
      img.style.cursor = 'pointer';
      const fn = () => handleImageClick(img.src);
      img.addEventListener('click', fn);
      handlers.push([img, fn]);
    });
    return () => {
      handlers.forEach(([img, fn]) => img.removeEventListener('click', fn));
    };
  }, [messages]);

  const ownerName = useMemo(() => resolveOwnerName(chat), [chat]);
  const createdLabel = chat?.createdAt ? datetimeToClient(chat.createdAt) : '';
  const headerSubtitle = useMemo(() => {
    // Data fora do i18n: o escape de interpolação transforma "/" em entidades HTML (ex. &#x2F;).
    if (ownerName && createdLabel) {
      return i18n.t('chatInternal.header.subtitle', { name: ownerName }) + createdLabel;
    }
    if (ownerName) return i18n.t('chatInternal.header.subtitleOwnerOnly', { name: ownerName });
    if (createdLabel) return i18n.t('chatInternal.header.subtitleDateOnly') + createdLabel;
    return '';
  }, [ownerName, createdLabel]);

  return (
    <>
      <Paper className={classes.root} elevation={0} square>
        <Toolbar variant="dense" className={classes.header}>
          {typeof onBack === 'function' && (
            <IconButton
              edge="start"
              className={classes.backBtn}
              onClick={onBack}
              aria-label={i18n.t('chatInternal.header.back')}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box minWidth={0} flex={1}>
            <Typography className={classes.headerTitle} component="h2" noWrap>
              {chat?.title || 'Conversa'}
            </Typography>
            {headerSubtitle ? (
              <Typography className={classes.headerSubtitle} title={headerSubtitle} noWrap>
                {headerSubtitle}
              </Typography>
            ) : null}
          </Box>
        </Toolbar>

        <div
          ref={messageListRef}
          onScroll={handleScroll}
          className={classes.messageList}
          role="log"
          aria-live="polite"
        >
          {Array.isArray(messages) &&
            messages.map((item, key) => {
              const mine = item.senderId === user.id;
              const html = item.message || '';
              const plainCaption = stripInternalChatMessageCaption(html);
              const rawMedia = item.mediaPath || item.mediaUrl;
              const hasMedia = Boolean(rawMedia);
              const mediaKind = hasMedia
                ? getInternalChatMediaKind(rawMedia, item.mediaName)
                : null;
              const isAudio = mediaKind === 'audio';
              const hasNonAudioMedia = hasMedia && !isAudio;
              const hasCaption = Boolean(plainCaption);
              const audioOnly = isAudio && !hasCaption && !hasNonAudioMedia;
              const showBubble =
                hasNonAudioMedia ||
                (!hasMedia && hasCaption) ||
                (hasMedia && hasCaption) ||
                (!mine && !audioOnly);

              return (
                <Box
                  key={item.id != null ? item.id : key}
                  className={`${classes.row} ${mine ? classes.rowMine : ''}`}
                >
                  {!mine && (
                    <Avatar className={classes.avatarSm}>{initials(item.sender?.name)}</Avatar>
                  )}
                  <Box
                    className={`${classes.messageColumn} ${
                      mine ? classes.messageColumnMine : classes.messageColumnTheirs
                    }`}
                  >
                    {!mine && audioOnly && item.sender?.name ? (
                      <Typography component="div" className={classes.senderAboveVoice}>
                        {item.sender?.name}
                      </Typography>
                    ) : null}
                    {showBubble ? (
                      <Box
                        className={`${classes.bubble} ${mine ? classes.bubbleOutgoing : classes.bubbleIncoming}`}
                      >
                        {!mine && (
                          <Typography className={classes.senderName} component="div">
                            {item.sender?.name}
                          </Typography>
                        )}
                        {hasMedia && plainCaption ? (
                          <Typography component="div" className={classes.messageCaptionPlain}>
                            {plainCaption}
                          </Typography>
                        ) : null}
                        {!hasMedia && plainCaption ? (
                          <div
                            className={`${classes.messageContent} internal-chat-msg`}
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        ) : null}
                        {hasNonAudioMedia ? (
                          <InternalChatMessageMedia
                            mediaPath={rawMedia}
                            mediaName={item.mediaName}
                            bubbleTone={mine ? 'outgoing' : 'incoming'}
                          />
                        ) : null}
                        {!isAudio ? (
                          <Box className={classes.timeCaptionRow} component="div">
                            <Typography
                              className={classes.timeCaption}
                              component="span"
                              style={{ marginTop: 0 }}
                            >
                              {datetimeToClient(item.createdAt)}
                            </Typography>
                            {mine &&
                            item.id != null &&
                            typeof handleDeleteMessage === 'function' ? (
                              <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                                <IconButton
                                  size="small"
                                  className={classes.messageDeleteIcon}
                                  aria-label={i18n.t('chatInternal.deleteMessage')}
                                  onClick={() => handleDeleteMessage(item.id)}
                                  disabled={loading}
                                >
                                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    ) : null}
                    {isAudio ? (
                      <InternalChatVoiceNote
                        mediaPath={rawMedia}
                        mediaName={item.mediaName}
                        speakerName={mine ? user?.name : item.sender?.name}
                        mine={mine}
                        sender={item.sender}
                      />
                    ) : null}
                    {isAudio ? (
                      <Box
                        component="div"
                        className={`${classes.timeBelowVoice} ${
                          mine ? classes.timeBelowVoiceMine : classes.timeBelowVoiceTheirs
                        }`}
                      >
                        <Typography
                          component="span"
                          variant="inherit"
                          style={{ fontSize: 'inherit', color: 'inherit' }}
                        >
                          {datetimeToClient(item.createdAt)}
                        </Typography>
                        {mine && item.id != null && typeof handleDeleteMessage === 'function' ? (
                          <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                            <IconButton
                              size="small"
                              className={classes.messageDeleteIcon}
                              aria-label={i18n.t('chatInternal.deleteMessage')}
                              onClick={() => handleDeleteMessage(item.id)}
                              disabled={loading}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    ) : null}
                  </Box>
                  {mine && <Avatar className={classes.avatarSm}>{initials(user?.name)}</Avatar>}
                </Box>
              );
            })}
          <div ref={baseRef} />
        </div>

        <div className={classes.composer}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*,video/*,audio/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setAttachmentFile(f);
                setAttachmentPreviewModalOpen(true);
              }
            }}
          />
          <Box className={classes.composerRow}>
            {!isRecording ? (
              <Tooltip title={i18n.t('chatInternal.media.attach')}>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  aria-label={i18n.t('chatInternal.media.attach')}
                  className={classes.actionIconMuted}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Box className={classes.composerInputShell}>
              {isRecording ? (
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                  placeholder={i18n.t('chatInternal.media.recordingPlaceholder')}
                  value=""
                  className={classes.composerTextField}
                  InputProps={{ readOnly: true, notched: false }}
                />
              ) : (
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Digite uma mensagem…"
                  value={contentMessage}
                  onChange={(e) => setContentMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className={classes.composerTextField}
                  multiline
                  minRows={1}
                  maxRows={6}
                  inputRef={composerInputElRef}
                  InputProps={{ notched: false }}
                />
              )}
            </Box>
            {isRecording ? (
              <Box className={classes.recordingChronoOutside} aria-live="polite">
                <span className={classes.recordingDot} aria-hidden />
                <Typography component="span" className={classes.recordingTime}>
                  {formatRecordingClock(recordingSeconds)}
                </Typography>
              </Box>
            ) : null}
            <Box className={classes.recorderActions}>
              {isRecording ? (
                <>
                  <Tooltip title={i18n.t('chatInternal.media.recordCancel')}>
                    <IconButton
                      size="small"
                      onClick={cancelRecording}
                      disabled={loading}
                      aria-label={i18n.t('chatInternal.media.recordCancel')}
                    >
                      <HighlightOffIcon className={classes.cancelRecordIcon} fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t('chatInternal.media.recordConfirm')}>
                    <IconButton
                      size="small"
                      onClick={confirmRecording}
                      disabled={loading}
                      aria-label={i18n.t('chatInternal.media.recordConfirm')}
                    >
                      <CheckCircleOutlineIcon
                        className={classes.confirmRecordIcon}
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>
                </>
              ) : contentMessage.trim() || attachmentFile ? (
                <Tooltip title={i18n.t('chatInternal.media.send')}>
                  <span className={classes.composerIconWrap}>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={handleSend}
                      disabled={
                        loading ||
                        (!contentMessage.trim() && !attachmentFile) ||
                        attachmentPreviewModalOpen
                      }
                      aria-label={i18n.t('chatInternal.media.send')}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={i18n.t('chatInternal.media.recordAudio')}>
                  <span className={classes.composerIconWrap}>
                    <IconButton
                      size="small"
                      onClick={handleStartVoice}
                      disabled={loading}
                      aria-label={i18n.t('chatInternal.media.recordAudio')}
                      className={classes.actionIconMuted}
                    >
                      <MicIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        </div>

        <Modal
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          className={classes.modal}
        >
          <div className={classes.modalContent}>
            <img
              src={selectedImage || ''}
              alt="Visualização ampliada"
              className={classes.modalImage}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={() => {
                if (selectedImage) {
                  const link = document.createElement('a');
                  link.href = selectedImage;
                  link.download = 'imagem.png';
                  link.click();
                }
              }}
            >
              Baixar imagem
            </Button>
          </div>
        </Modal>
      </Paper>
      <InternalChatAttachmentPreviewModal
        open={attachmentPreviewModalOpen}
        file={attachmentFile}
        onDiscard={() => {
          setAttachmentFile(null);
          setAttachmentPreviewModalOpen(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        onSend={() => {
          handleSend();
        }}
        sendDisabled={loading}
      />
    </>
  );
}
